// render-kin-family.ts — the "kin-family" renderer: ONE creature's immediate family, plus the
// authoring widget for it.
//
// ⭐ THIS IS THE ONE COMPONENT, MOUNTED IN THREE PLACES (owner ruling 2026-08-20): the catalog
// creature's console, a placed creature's GM panel, and anywhere else a Family block belongs. The
// mount carries only `kind` + `id`; everything else — what may be seen, what may be edited, whether
// a placement is inheriting — is decided by options/api/kin_inc.php and merely rendered here.
//
// WHY IT FETCHES RATHER THAN READING THE BLOB
// Family edges are NOT item layers (that was the whole design ruling), so they are not in the
// compiled $item_arr and cannot be. Stamping them in at compile time would put a DB read on every
// creature compile and bake genealogy into the Redis blob, where a catalog edit could not reach it.
// One lazy request per creature page is the cheaper and more honest trade.
//
// THE SHAPES THIS HAS TO DRAW, all of which come from the paper fixture (docs/lore/02-pantheon.md):
//   · a DISPUTED parent — two "mother" edges to different creatures, neither one winning
//   · a SECRET edge, visible only to someone who could author it, and marked as such so its owner
//     can see at a glance what a player cannot
//   · full vs HALF siblings, where "half" is computed over undisputed edges on BOTH sides
//   · an INHERITED family on a placement — borrowed from the catalog creature until a GM overrides
//   · adoptive / step / foster qualifiers, which a bare parent pointer could never carry

import type { CatalogListConfig, KinFamilyResponse, KinNode } from './types';
import { initTooltips, resolveUrl, results, setStatus, showLoader, hideLoader } from './catalog-list';

function icon(id: string, w = 18, h = 18, title: string | boolean = false): string {
    return typeof iconMap === 'function' ? iconMap(id, w, h, title) : '';
}

function el(tag: string, className = '', text = ''): HTMLElement {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
}

// ── Wording ─────────────────────────────────────────────────────────────────────────────────
// The role is the label wherever one exists, because "mother" says more than "parent" and the
// pantheon distinguishes a SPOUSE (Comta, the Queen) from a CONSORT (Amotl, Aspabe, Aesas…).
function roleLabel(node: KinNode): string {
    const role = (node.role || '').trim();
    if (role) return role.charAt(0).toUpperCase() + role.slice(1);
    return node.relation === 'union' ? 'Partner' : 'Parent';
}

// A qualifier is only worth showing when it is NOT the default. "Birth mother" is noise; "Adoptive
// mother" is the entire point of the field.
function qualifierLabel(node: KinNode): string {
    const q = (node.qualifier || '').trim();
    return (!q || q === 'birth') ? '' : q.charAt(0).toUpperCase() + q.slice(1);
}

function siblingLabel(node: KinNode): string {
    if (node.half === 1) {
        const shared = node.shared_parents;
        return shared ? `Half-sibling · ${shared} shared parent${shared === 1 ? '' : 's'}` : 'Half-sibling';
    }
    return 'Sibling';
}

// ── One row ─────────────────────────────────────────────────────────────────────────────────
function buildRow(
    node: KinNode,
    label: string,
    canAuthor: boolean,
    onRemove: (id: string) => void,
    onEdit: (node: KinNode, anchor: HTMLElement) => void,
    // Suppresses the qualifier badge when the row already sits under a heading that names it —
    // "Adoptive" under "Parents (Adoptive)" is the same word twice.
    suppressQualifier = false,
): HTMLElement {

    // A STACKED CARD, NOT A ONE-LINE ROW (owner, 2026-08-21). Role, name, dates, three badges and a
    // delete button on one flex line is fine at 900px and a wrapping blob in the ~380px sidebar this
    // panel actually lives in. Three bands instead:
    //     role - NAME across the full width
    //            dates - status - badges
    //            [controls, revealed on hover]
    const row = el('div', 'ft-kin-row ft-kin-card');
    if (node.disputed) row.classList.add('ft-kin-row--disputed');
    if (node.inherited) row.classList.add('ft-kin-row--inherited');

    const head = el('div', 'ft-kin-card-head');
    head.appendChild(el('span', 'ft-kin-role', label));
    row.appendChild(head);

    const meta = el('div', 'ft-kin-card-meta');

    // A hidden node keeps its place in the tree but not its name — a public edge may legitimately
    // point at a private creature, and the edge being visible does not make the creature visible.
    if (node.hidden) {
        const unknown = el('span', 'ft-kin-name ft-kin-name--hidden fst-italic text-muted', 'Unknown');
        unknown.setAttribute('data-bs-toggle', 'tooltip');
        unknown.setAttribute('data-bs-original-title', 'This creature is not public.');
        head.appendChild(unknown);
    } else {
        const link = document.createElement('a');
        link.className = 'ft-kin-name';
        link.textContent = node.name || 'Unnamed';
        // ⚠ An ARTICLE node has no item_id — articles are the third kin kind and are not `items`.
        // The item/encounter pair is not exhaustive, and routing an article through the encounter
        // branch produced `encounter.php?id=` and a link to nowhere.
        // ⚠ PREFER THE SERVER'S URL for an article: its readable address contains a slug this bundle
        // cannot derive from an id. ft_kin_node_arr() resolves it and ships it on the node; the id
        // form is the fallback for a node resolved before that field existed.
        link.href = node.kind === 'article'
            ? (node.url || `article.php?id=${encodeURIComponent(String(node.id))}`)
            : node.kind === 'item'
                ? `item.php?id=${encodeURIComponent(String(node.item_id || node.id))}`
                : `encounter.php?id=${encodeURIComponent(String(node.encounter_id || ''))}`;
        head.appendChild(link);

        // ⭐ Dates inline, right after the name. The panel is a LIST, so unlike the diagram it has
        // horizontal room — but it still gets the terse form, because a relative's dates are context
        // for the relationship, not the subject of the row.
        const life = node.life;
        if (life) {
            const dates = life.eternal
                ? '∞'
                : (life.birth && life.death)
                    ? `${life.birth} – ${life.death}`
                    : life.birth
                        ? (life.is_dead ? `${life.birth} – ?` : life.birth)
                        : (life.death ? `d. ${life.death}` : '');

            if (dates) {
                const span = el('span', 'ft-kin-row-dates text-muted small', dates);
                if (life.age !== null && life.age !== undefined) {
                    span.setAttribute('data-bs-toggle', 'tooltip');
                    span.setAttribute(
                        'data-bs-original-title',
                        life.age_source === 'at_death' ? `${life.age} years old at death` : `${life.age} years old`,
                    );
                }
                meta.appendChild(span);
            }

            // ⚠ The skull is the DEAD marker and it is deliberately not a word: it has to survive
            // being scanned down a column of twenty relatives.
            if (life.is_dead) {
                const skull = el('span', 'ft-kin-row-dead', '☠');
                skull.setAttribute('data-bs-toggle', 'tooltip');
                skull.setAttribute('data-bs-original-title', 'Dead');
                meta.appendChild(skull);
            }
        }
    }

    const qualifier = suppressQualifier ? '' : qualifierLabel(node);
    if (qualifier) {
        meta.appendChild(el('span', 'badge bg-secondary ft-kin-badge', qualifier));
    }

    // ⚠ A disputed edge is NOT an error state and must not read like one. The source records two
    // mothers for Ivona and refuses to pick; the UI's job is to show both and say so.
    if (node.disputed) {
        const badge = el('span', 'badge bg-warning text-dark ft-kin-badge', 'Disputed');
        if (node.note) {
            badge.setAttribute('data-bs-toggle', 'tooltip');
            badge.setAttribute('data-bs-original-title', node.note);
        }
        meta.appendChild(badge);
    }

    // Only ever rendered for a viewer who can already see the edge, so this reveals nothing — it
    // tells an AUTHOR which of their own edges a player cannot see.
    if (node.edge_private > 0) {
        const badge = el('span', 'badge bg-dark ft-kin-badge', node.edge_private === 2 ? 'Secret' : 'Private');
        badge.setAttribute('data-bs-toggle', 'tooltip');
        badge.setAttribute('data-bs-original-title', 'Hidden from players.');
        meta.appendChild(badge);
    }

    if (node.inherited) {
        const badge = el('span', 'badge bg-info text-dark ft-kin-badge', 'Inherited');
        badge.setAttribute('data-bs-toggle', 'tooltip');
        badge.setAttribute('data-bs-original-title', 'From the catalog creature. Editing here makes this placement its own.');
        meta.appendChild(badge);
    }

    // Siblings are DERIVED — there is no sibling row to delete — so they never get a remove button.
    // ⚠ `node.editable` is the SERVER's per-EDGE judgement; `canAuthor` is only about the focus.
    // A parent edge is stored on the CHILD, so being able to author this creature does not mean you
    // may delete a row hanging off one of its children. Require both.
    if (meta.childElementCount) row.appendChild(meta);

    // CONTROLS ON HOVER ONLY. A delete button beside every relative turns a family into a
    // spreadsheet of hazards, and it is the least-used thing on the card. The band is
    // visibility-hidden rather than display:none, so revealing it never reflows the list.
    if (canAuthor && node.editable === 1 && node.kin_link_id && node.relation) {

        const controls = el('div', 'ft-kin-card-controls');

        const edit = el('button', 'btn btn-sm ft-kin-card-btn');
        edit.setAttribute('type', 'button');
        edit.setAttribute('aria-label', `Edit this ${label.toLowerCase()} link`);
        edit.innerHTML = (icon('edit', 13, 13, false) || '\u270e') + '<span>Edit</span>';
        edit.addEventListener('click', () => onEdit(node, row));
        controls.appendChild(edit);

        // \u2b50\u2b50 REMOVE IS TWO-STEP, IN PLACE. Deleting a parent edge silently re-shapes the whole
        // tree -- siblings recompute, a generation band can empty -- and the row it hangs off is one
        // of forty in a hover list, so a mis-click is cheap to make and expensive to notice.
        //
        // \u26a0 Deliberately NOT window.confirm(): a modal steals focus, cannot say WHICH relative in
        // its own styling, and on a hover-revealed control the dialog's own appearance moves the
        // cursor off the card. The button becomes its own confirmation and reverts after a few
        // seconds, so an abandoned click cannot leave a live delete armed under the pointer.
        const remove = el('button', 'btn btn-sm ft-kin-card-btn ft-kin-remove');
        remove.setAttribute('type', 'button');
        remove.setAttribute('aria-label', `Remove ${label}`);
        const restIcon = (icon('delete', 13, 13, false) || '&times;');
        remove.innerHTML = restIcon + '<span>Remove</span>';

        let armed = false;
        let armedTimer: number | undefined;
        const disarm = () => {
            armed = false;
            window.clearTimeout(armedTimer);
            remove.classList.remove('is-armed');
            remove.innerHTML = restIcon + '<span>Remove</span>';
        };

        remove.addEventListener('click', () => {
            if (armed) {
                disarm();
                onRemove(String(node.kin_link_id));
                return;
            }
            armed = true;
            remove.classList.add('is-armed');
            remove.innerHTML = restIcon + '<span>Sure?</span>';
            armedTimer = window.setTimeout(disarm, 4000);
        });
        // Moving off the card abandons the confirmation \u2014 an armed delete must never survive the
        // pointer leaving it.
        row.addEventListener('mouseleave', disarm);

        controls.appendChild(remove);
        row.appendChild(controls);
    }

    return row;
}

// Each section gets its OWN glyph so the four bands are told apart at a glance rather than by
// reading four near-identical grey words. All four alias onto catalog art that already carries the
// idea, so nothing needed minting.
const SECTION_ICON: Record<string, string> = {
    parents: 'ancestry',
    partners: 'bond',
    children: 'creature',
    siblings: 'family_tree',
};

// \u2b50 THE INLINE EDGE EDITOR. `ft_kin_author_edge()` is an UPSERT keyed on the endpoint tuple, so
// re-authoring the same (source, target, relation) with new metadata edits the existing row in place
// -- no new endpoint was needed, only a form pointed at the one that already exists.
//
// \u26a0 What is editable is exactly the metadata: role, qualifier, disputed, visibility, note. The
// ENDPOINTS are not, and must not be -- changing who the relative IS is a different edge, and
// offering it here would silently delete one relationship and create another under an Edit button.
function buildEdgeEditor(
    node: KinNode,
    vocab: KinFamilyResponse['vocabulary'],
    onSave: (payload: Record<string, unknown>) => void,
    onCancel: () => void,
): HTMLElement {

    const vlist = (value: unknown): string[] => (Array.isArray(value) ? value as string[] : []);
    const box = el('form', 'ft-kin-edge-editor');

    const line = el('div', 'd-flex flex-wrap align-items-center gap-2 mb-2');

    const roleSel = document.createElement('select');
    roleSel.className = 'form-select form-select-sm w-auto';
    const roles = node.relation === 'union' ? vlist(vocab?.union_roles) : vlist(vocab?.parent_roles);
    roles.forEach((r) => roleSel.appendChild(option(r)));
    if (node.role) roleSel.value = String(node.role);
    line.appendChild(roleSel);

    // A union has no birth/adoptive/step distinction -- only a parent edge does.
    const qualifierSel = document.createElement('select');
    qualifierSel.className = 'form-select form-select-sm w-auto';
    vlist(vocab?.qualifiers).forEach((q) => qualifierSel.appendChild(option(q)));
    if (node.qualifier) qualifierSel.value = String(node.qualifier);
    if (node.relation !== 'union') line.appendChild(qualifierSel);

    const privateSel = document.createElement('select');
    privateSel.className = 'form-select form-select-sm w-auto';
    privateSel.appendChild(option('0', 'Public'));
    privateSel.appendChild(option('1', 'Private'));
    privateSel.appendChild(option('2', 'Secret'));
    privateSel.value = String(node.edge_private ?? 0);
    line.appendChild(privateSel);

    const disputedWrap = el('div', 'form-check form-check-inline m-0');
    const disputed = document.createElement('input');
    disputed.type = 'checkbox';
    disputed.className = 'form-check-input';
    disputed.id = `ft_kin_edit_disputed_${Math.random().toString(36).slice(2)}`;
    disputed.checked = node.disputed === 1;
    const disputedLabel = el('label', 'form-check-label small', 'Disputed');
    disputedLabel.setAttribute('for', disputed.id);
    disputedWrap.appendChild(disputed);
    disputedWrap.appendChild(disputedLabel);
    line.appendChild(disputedWrap);

    box.appendChild(line);

    const note = document.createElement('input');
    note.type = 'text';
    note.className = 'form-control form-control-sm mb-2';
    note.placeholder = 'Note \u2014 why the record says this';
    note.value = String(node.note || '');
    box.appendChild(note);

    const actions = el('div', 'd-flex align-items-center gap-2');
    const save = el('button', 'btn btn-sm btn-primary', 'Save') as HTMLButtonElement;
    save.type = 'submit';
    const cancel = el('button', 'btn btn-sm btn-link text-muted', 'Cancel') as HTMLButtonElement;
    cancel.type = 'button';
    cancel.addEventListener('click', onCancel);
    actions.appendChild(save);
    actions.appendChild(cancel);
    box.appendChild(actions);

    box.addEventListener('submit', (event) => {
        event.preventDefault();
        onSave({
            // ⚠ ONLY THE ID AND THE METADATA. The endpoints are deliberately NOT sent: a parent
            // edge hangs off the CHILD, so the "other end" a row shows is the SOURCE in the Children
            // section and the TARGET in the Parents section. Working that out per-section in the
            // browser is exactly how you ship a REVERSED edge — a child recorded as its own
            // parent's parent. The server's `edit` action re-reads the stored row and re-authors its
            // own endpoints; only the metadata below comes from here.
            kin_link_id: node.kin_link_id,
            role: roleSel.value,
            qualifier: qualifierSel.value,
            disputed: disputed.checked ? 1 : 0,
            private: Number(privateSel.value) || 0,
            note: note.value,
        });
    });

    return box;
}

function buildSection(title: string, rows: HTMLElement[]): HTMLElement | null {
    if (!rows.length) return null;
    const section = el('div', 'ft-kin-section');

    // A HEADER BAND, not a bold word. Same reasoning as the article panel heads: a label floating
    // over its own rows reads as part of them; a tinted band with a rule under it reads as a header,
    // which is what makes four stacked sections scan as four sections.
    const key = (title.toLowerCase().split(' ')[0] || '').replace(/[^a-z]/g, '');
    const head = el('div', `ft-kin-section-title ft-kin-section-head ft-kin-section-head--${key}`);
    const glyph = SECTION_ICON[key];
    if (glyph) {
        const g = el('span', 'ft-kin-section-glyph');
        g.innerHTML = icon(glyph, 14, 14, false);
        head.appendChild(g);
    }
    head.appendChild(el('span', 'ft-kin-section-label', title));
    section.appendChild(head);

    rows.forEach((r) => section.appendChild(r));
    return section;
}

// ══════════════════════════════════════════════════════════════════════════════════════════════
// The add form
// ══════════════════════════════════════════════════════════════════════════════════════════════

// The site's search mark is an inline SVG (includes/search_inline_trailing_icons_inc.php), not an
// entry in the icon catalog -- there is no 'search' name to pass to icon(). Copied verbatim so the
// picker's spyglass is pixel-identical to every other search field on the site.
const SPYGLASS_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true" focusable="false"><circle cx="11" cy="11" r="7"/><path d="M16 16l4.5 4.5"/></svg>';

type PickerHit = {
    id: string;
    kind: string;
    name: string;
    kicker?: string;   // "Deity", "Creature", "Category" -- what tells two same-named hits apart
    icon?: string;
    private?: number;
};

function option(value: string, label?: string): HTMLOptionElement {
    const o = document.createElement('option');
    o.value = value;
    o.textContent = label ?? (value.charAt(0).toUpperCase() + value.slice(1));
    return o;
}

function buildAddForm(
    data: KinFamilyResponse,
    onAdd: (payload: Record<string, unknown>) => void,
    csrf: string,
): HTMLElement {

    const form = el('form', 'ft-kin-add border-top pt-2 mt-2');

    const line1 = el('div', 'd-flex flex-wrap align-items-center gap-2 mb-2');

    // The vocabulary comes from the server; treat every list as optional so a partial payload
    // renders an empty picker rather than throwing and blanking the whole pane.
    const vocab = data.vocabulary || ({} as KinFamilyResponse['vocabulary']);
    const vlist = (value: unknown): string[] => (Array.isArray(value) ? value as string[] : []);

    const relationSel = document.createElement('select');
    relationSel.className = 'form-select form-select-sm w-auto';
    vlist(vocab.relations).forEach((r) => relationSel.appendChild(option(r, r === 'union' ? 'Partner' : 'Parent')));

    const roleSel = document.createElement('select');
    roleSel.className = 'form-select form-select-sm w-auto';

    // The role vocabulary depends on the relation, so it is rebuilt whenever the relation changes.
    // ⭐ 'parent' stays in the parent list on purpose — the Titan Leram's sex is recorded as "—",
    // and a two-mother or unknown-gender pairing has to be authorable without lying about it.
    const fillRoles = () => {
        roleSel.textContent = '';
        const roles = relationSel.value === 'union' ? vlist(vocab.union_roles) : vlist(vocab.parent_roles);
        roles.forEach((r) => roleSel.appendChild(option(r)));
    };
    relationSel.addEventListener('change', fillRoles);
    fillRoles();

    line1.appendChild(relationSel);
    line1.appendChild(roleSel);

    // -- The relative picker ----------------------------------------------------------------
    // A REAL AUTOCOMPLETE, NOT A <datalist> (owner, 2026-08-21). The endpoint now returns this
    // world's ARTICLES as well as the creature catalog, and the two need telling apart -- a
    // datalist can only render a string, so "Comes, God of the Heavens" the article and a creature
    // of the same name would be indistinguishable. This dropdown shows each hit's type and glyph.
    //
    // The spyglass is the SAME inline SVG includes/search_inline_trailing_icons_inc.php draws, not
    // an icon-catalog PNG -- the site's search mark is an SVG and there is no 'search' name in
    // SimpleCustomIcon::LIST to reach for. Copied so the two are pixel-identical.
    const searchWrap = el('div', 'position-relative flex-grow-1 min-w-0 ft-kin-picker');
    const search = document.createElement('input');
    search.type = 'search';
    search.className = 'form-control form-control-sm ft-kin-picker-input';
    search.placeholder = 'Search this world\u2026';
    search.setAttribute('autocomplete', 'off');
    search.setAttribute('role', 'combobox');
    search.setAttribute('aria-expanded', 'false');
    searchWrap.appendChild(search);

    const spy = el('span', 'ft-kin-picker-spy');
    spy.innerHTML = SPYGLASS_SVG;
    searchWrap.appendChild(spy);

    const menu = el('div', 'ft-kin-picker-menu');
    menu.setAttribute('role', 'listbox');
    searchWrap.appendChild(menu);
    line1.appendChild(searchWrap);

    let chosenId = '';
    let chosenKind = 'item';

    const closeMenu = () => {
        menu.classList.remove('is-open');
        search.setAttribute('aria-expanded', 'false');
    };

    // A click INSIDE the menu must not close it before the option's own handler runs, so this
    // listens on pointerdown OUTSIDE the wrapper rather than on the input's blur.
    document.addEventListener('pointerdown', (event) => {
        if (!searchWrap.contains(event.target as Node)) closeMenu();
    });

    const renderMenu = (hits: PickerHit[]): void => {
        menu.textContent = '';
        if (!hits.length) {
            menu.appendChild(el('div', 'ft-kin-picker-empty', 'Nothing here by that name.'));
        } else {
            hits.forEach((hit) => {
                const opt = el('button', 'ft-kin-picker-opt') as HTMLButtonElement;
                opt.type = 'button';
                opt.setAttribute('role', 'option');

                const glyph = el('span', 'ft-kin-picker-glyph');
                glyph.innerHTML = icon(hit.icon || (hit.kind === 'article' ? 'article' : 'creature'), 16, 16, false);
                opt.appendChild(glyph);

                const text = el('span', 'ft-kin-picker-text');
                text.appendChild(el('span', 'ft-kin-picker-name', hit.name || 'Unnamed'));
                if (hit.kicker) text.appendChild(el('span', 'ft-kin-picker-kicker', hit.kicker));
                opt.appendChild(text);

                // Only an author ever sees this list, so flagging a non-public entry tells them
                // what a player would not see -- it reveals nothing new.
                if (hit.private) {
                    opt.appendChild(el('span', 'badge bg-dark ft-kin-badge ms-auto', hit.private === 2 ? 'Secret' : 'Private'));
                }

                opt.addEventListener('click', () => {
                    chosenId = hit.id;
                    chosenKind = hit.kind;
                    search.value = hit.name;
                    closeMenu();
                });
                menu.appendChild(opt);
            });
        }
        menu.classList.add('is-open');
        search.setAttribute('aria-expanded', 'true');
    };

    let timer: number | undefined;
    search.addEventListener('input', () => {
        // Typing after a pick INVALIDATES it. Leaving chosenId set would submit the previous
        // choice while the field shows a different name.
        chosenId = '';
        window.clearTimeout(timer);
        const term = search.value.trim();
        if (term.length < 2) {
            closeMenu();
            return;
        }
        timer = window.setTimeout(async () => {
            try {
                const res = await fetch(resolveUrl('options/api/kin_inc.php'), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ action: 'search', kind: data.node.kind, id: data.node.id, term, csrf_token: csrf }),
                    credentials: 'same-origin',
                });
                const json = await res.json().catch(() => ({}));
                renderMenu(Array.isArray(json.results) ? json.results : []);
            } catch {
                /* a failed lookup just means no suggestions — never break the form */
                closeMenu();
            }
        }, 250);
    });

    // Re-opening on focus saves retyping when the author picked the wrong one first time.
    search.addEventListener('focus', () => {
        if (menu.childElementCount && search.value.trim().length >= 2) menu.classList.add('is-open');
    });
    search.addEventListener('keydown', (event) => {
        if ((event as KeyboardEvent).key === 'Escape') closeMenu();
    });

    form.appendChild(line1);

    // ── Line 2: the metadata a layer could never have carried ───────────────────────────────
    const line2 = el('div', 'd-flex flex-wrap align-items-center gap-2');

    const qualifierSel = document.createElement('select');
    qualifierSel.className = 'form-select form-select-sm w-auto';
    vlist(vocab.qualifiers).forEach((q) => qualifierSel.appendChild(option(q)));
    line2.appendChild(qualifierSel);

    const privateSel = document.createElement('select');
    privateSel.className = 'form-select form-select-sm w-auto';
    privateSel.appendChild(option('0', 'Public'));
    privateSel.appendChild(option('1', 'Private'));
    privateSel.appendChild(option('2', 'Secret'));
    line2.appendChild(privateSel);

    const disputedWrap = el('div', 'form-check form-check-inline m-0');
    const disputed = document.createElement('input');
    disputed.type = 'checkbox';
    disputed.className = 'form-check-input';
    disputed.id = `ft_kin_disputed_${Math.random().toString(36).slice(2)}`;
    const disputedLabel = el('label', 'form-check-label small', 'Disputed');
    disputedLabel.setAttribute('for', disputed.id);
    disputedLabel.setAttribute('data-bs-toggle', 'tooltip');
    disputedLabel.setAttribute(
        'data-bs-original-title',
        'The records disagree. Add BOTH claims and mark them disputed rather than picking a winner.',
    );
    disputedWrap.appendChild(disputed);
    disputedWrap.appendChild(disputedLabel);
    line2.appendChild(disputedWrap);

    const note = document.createElement('input');
    note.type = 'text';
    note.className = 'form-control form-control-sm flex-grow-1 min-w-0';
    note.placeholder = 'Note (optional) — e.g. "the prose says otherwise"';
    note.maxLength = 512;
    line2.appendChild(note);

    const submit = el('button', 'btn btn-sm btn-primary flex-shrink-0', 'Add');
    submit.setAttribute('type', 'submit');
    line2.appendChild(submit);

    form.appendChild(line2);

    form.addEventListener('submit', (event) => {
        event.preventDefault();
        if (!chosenId) {
            search.classList.add('is-invalid');
            search.focus();
            return;
        }
        search.classList.remove('is-invalid');
        onAdd({
            relation: relationSel.value,
            role: roleSel.value,
            target_kind: chosenKind,
            target_id: chosenId,
            qualifier: qualifierSel.value,
            disputed: disputed.checked ? 1 : 0,
            note: note.value.trim(),
            private: parseInt(privateSel.value, 10) || 0,
        });
    });

    return form;
}

// ══════════════════════════════════════════════════════════════════════════════════════════════
// Mount
// ══════════════════════════════════════════════════════════════════════════════════════════════

export function mountCatalogKinFamily(root: HTMLElement, config: CatalogListConfig): void {

    const container = results(root);
    if (!container) return;

    const kind = root.getAttribute('data-ft-kin-kind') || 'item';
    const id = root.getAttribute('data-ft-kin-id') || '';
    const emptyText = root.getAttribute('data-ft-empty-text') || 'No family recorded.';
    // Writes are POST + CSRF. The server emits this only for a viewer who can author, so its
    // absence is the normal read-only case, not an error.
    const csrfToken = root.getAttribute('data-ft-kin-csrf') || '';
    // Edit permission and authoring-surface intent are separate. A GM may be allowed to edit a
    // creature while viewing its ordinary card; that must not turn the card itself into an editor.
    const readOnly = root.getAttribute('data-ft-kin-readonly') === '1';

    if (!id) {
        setStatus(root, 'No creature specified.');
        return;
    }

    const post = async (payload: Record<string, unknown>): Promise<any> => {
        const res = await fetch(resolveUrl(config.endpoint || 'options/api/kin_inc.php'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ kind, id, csrf_token: csrfToken, ...payload }),
            credentials: 'same-origin',
        });
        const json = await res.json().catch(() => ({}));
        // ⚠ REQUIRE ok === true, not merely a 200. A 200 carrying `{}` was being treated as success:
        // the widget reloaded, showed nothing changed, and reported no error — the worst outcome,
        // because the user cannot tell a no-op from a failure.
        if (!res.ok || json.error || json.ok !== true) {
            throw new Error(json.error || `HTTP ${res.status}`);
        }
        return json;
    };

    const showError = (message: string) => {
        const box = el('div', 'alert alert-danger py-1 px-2 small mt-2', message);
        container.appendChild(box);
        window.setTimeout(() => box.remove(), 8000);
    };

    // Warnings are the pantheon's whole point — "that creature is already recorded as a sibling"
    // must inform, never block. Shown in a distinct, calmer style than an error.
    const showWarnings = (warnings: string[]) => {
        if (!warnings || !warnings.length) return;
        const box = el('div', 'alert alert-warning py-1 px-2 small mt-2');
        warnings.forEach((w) => box.appendChild(el('div', '', w)));
        container.appendChild(box);
        window.setTimeout(() => box.remove(), 12000);
    };

    const build = (data: KinFamilyResponse): void => {

        container.textContent = '';

        const canAuthor = !readOnly && data.can_author === 1;

        const onRemove = async (kinLinkId: string) => {
            try {
                await post({ action: 'remove', kin_link_id: kinLinkId });
                void load();
            } catch (e) {
                showError(e instanceof Error ? e.message : 'Could not remove that relationship.');
            }
        };

        // ⭐ ONE EDITOR AT A TIME, opened in place under the card it belongs to. A dialog would lose
        // the "which relative is this" context the row itself provides.
        let openEditor: HTMLElement | null = null;
        const closeEditor = () => {
            openEditor?.remove();
            openEditor = null;
        };

        const onEdit = (node: KinNode, anchor: HTMLElement) => {
            closeEditor();
            const editor = buildEdgeEditor(
                node,
                data.vocabulary,
                async (payload) => {
                    try {
                        const result = await post({ action: 'edit', ...payload });
                        closeEditor();
                        await load();
                        showWarnings(result.warnings || []);
                    } catch (e) {
                        showError(e instanceof Error ? e.message : 'Could not save that relationship.');
                    }
                },
                closeEditor,
            );
            openEditor = editor;
            anchor.appendChild(editor);
        };

        const onAdd = async (payload: Record<string, unknown>) => {
            try {
                const result = await post({ action: 'add', ...payload });
                await load();
                showWarnings(result.warnings || []);
            } catch (e) {
                showError(e instanceof Error ? e.message : 'Could not add that relationship.');
            }
        };

        // Normalise before rendering: a truncated or unexpected payload must degrade to "nothing to
        // show", never throw inside .map() and take the whole pane down with it.
        const list = (value: unknown): KinNode[] => (Array.isArray(value) ? value as KinNode[] : []);

        // ⭐ ADOPTIVE PARENTS GET THEIR OWN HEADED GROUP, as World Anvil lists "Parents (Adopting)"
        // beneath "Parents". The qualifier was already stored and badged, but a badge inside one
        // long list is easy to miss — and for a fostered creature the distinction between who bore
        // them and who raised them is usually the more interesting half of the story.
        const parents = list(data.parents);
        const birthParents = parents.filter((n) => (n.qualifier || 'birth') === 'birth');
        const raisedParents = parents.filter((n) => (n.qualifier || 'birth') !== 'birth');

        const raisedHeading = (() => {
            const kinds = [...new Set(raisedParents.map((n) => n.qualifier))];
            // One qualifier → name it exactly ("Parents (Adopting)"). Several → the neutral heading,
            // and each row still carries its own badge.
            if (kinds.length === 1 && kinds[0]) {
                const q = String(kinds[0]);
                return `Parents (${q.charAt(0).toUpperCase()}${q.slice(1)})`;
            }
            return 'Parents (Other)';
        })();

        const sections: (HTMLElement | null)[] = [
            buildSection('Parents', birthParents.map((n) => buildRow(n, roleLabel(n), canAuthor, onRemove, onEdit))),
            // The heading carries the qualifier, so the per-row badge would only repeat it — unless
            // the group is the mixed "Parents (Other)" bucket, where each row's own badge is the
            // only thing saying which kind it is.
            buildSection(
                raisedHeading,
                raisedParents.map((n) => buildRow(n, roleLabel(n), canAuthor, onRemove, onEdit, raisedHeading !== 'Parents (Other)')),
            ),
            buildSection('Partners', list(data.partners).map((n) => buildRow(n, roleLabel(n), canAuthor, onRemove, onEdit))),
            buildSection('Children', list(data.children).map((n) => buildRow(n, 'Child', canAuthor, onRemove, onEdit))),
            // Siblings are derived from shared parents — nothing stores them, so nothing can edit
            // them here. Pass canAuthor=false so no delete button implies otherwise.
            buildSection('Siblings', list(data.siblings).map((n) => buildRow(n, siblingLabel(n), false, onRemove, onEdit))),
        ];

        const any = sections.some(Boolean);
        sections.forEach((s) => { if (s) container.appendChild(s); });

        if (!any) {
            container.appendChild(el('div', 'text-muted small', emptyText));
        }

        // A placement still borrowing its catalog creature's family says so, and offers the way out.
        if (data.is_inherited === 1 && any) {
            const banner = el(
                'div',
                'ft-kin-inherited-banner small text-muted border-top pt-2 mt-2',
                canAuthor
                    ? 'This family is inherited from the catalog creature. Editing it here makes this placement its own.'
                    : 'This family is inherited from the catalog creature.',
            );
            if (canAuthor) {
                const detach = el('button', 'btn btn-sm btn-link p-0 ms-1', 'Detach');
                detach.setAttribute('type', 'button');
                detach.setAttribute('data-bs-toggle', 'tooltip');
                detach.setAttribute('data-bs-original-title', 'Give this placement no family of its own, and stop inheriting.');
                detach.addEventListener('click', async () => {
                    try {
                        await post({ action: 'detach', relation: 'parent' });
                        void load();
                    } catch (e) {
                        showError(e instanceof Error ? e.message : 'Could not detach.');
                    }
                });
                banner.appendChild(detach);
            }
            container.appendChild(banner);
        }

        // A placement that has been overridden or detached can be handed back to the catalog.
        if (kind === 'encounter_creature' && data.is_inherited === 0 && canAuthor) {
            const restore = el('button', 'btn btn-sm btn-link p-0 mt-2', 'Restore inherited family');
            restore.setAttribute('type', 'button');
            restore.addEventListener('click', async () => {
                try {
                    await post({ action: 'reinherit' });
                    void load();
                } catch (e) {
                    showError(e instanceof Error ? e.message : 'Could not restore.');
                }
            });
            container.appendChild(restore);
        }

        if (canAuthor) {
            container.appendChild(buildAddForm(data, onAdd, csrfToken));
        }

        // The full tree is a separate page; the block is the summary, not the diagram.
        const treeLink = document.createElement('a');
        treeLink.className = 'btn btn-sm btn-outline-secondary mt-2';
        treeLink.href = `family_tree.php?kind=${encodeURIComponent(kind)}&id=${encodeURIComponent(id)}`;
        treeLink.innerHTML = `${icon('family_tree', 18, 18, false)} <span>View family tree</span>`;
        container.appendChild(treeLink);

        initTooltips(container);
    };

    const load = async (): Promise<void> => {
        try {
            const params = new URLSearchParams({ action: 'family', kind, id });
            const res = await fetch(`${resolveUrl(config.endpoint || 'options/api/kin_inc.php')}?${params.toString()}`, {
                credentials: 'same-origin',
            });
            const data: KinFamilyResponse = await res.json().catch(() => ({} as any));
            if (!res.ok || (data as any).error || (data as any).ok !== true || !data.node) {
                throw new Error((data as any).error || `HTTP ${res.status}`);
            }
            hideLoader(root);
            setStatus(root, '');
            build(data);
        } catch (e) {
            hideLoader(root);
            console.error('[kin-family] fetch/render failed', e);
            setStatus(root, 'Could not load this creature’s family.');
        }
    };

    showLoader(root, config);
    void load();
}
